import React, { useState, useEffect, useContext,  useMemo } from "react";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, InputLabel, MenuItem, Select, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { LoadingButton } from '@mui/lab';

import moment from "moment";
import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import { Utils } from "common/Utils";

import LoaderAnimation from "pages/LoaderAnimation";
import ConfirmDialog from "components/Content/Modal/ConfirmDialog";
import AximTooltip from "components/AximTooltip";
import ComplianceHours from "components/Compliance/ComplianceHours";
import ComplianceEnrollments from "components/Compliance/ComplianceEnrollments";
import ComplianceCompare from "components/Compliance/ComplianceCompare";
import CompleteCompliance from "components/Compliance/CompleteCompliance";

const formatDate = (date) => moment(date).format("MM/DD/YYYY");

export default function Compliance({ isVisible, isAdmin, isReadonly }) {
    const { companyId, error } = useContext(PrivateRoutesContext);

    const [activeStep, setActiveStep] = useState(-1);
    const [isLoading, setIsLoading] = useState(true);

    const [doShowView, setDoShowView] = useState(false);
    const [viewTitle, setViewTitle] = useState('');
    const [viewContent, setViewContent] = useState(null);

    const [compliance, setCompliance] = useState(null);
    const [lastCompletedSteps, setLastCompletedSteps] = useState(null);
    const [inProgressStep, setInProgressStep] = useState(null);
    const [isPolling, setIsPolling] = useState(null);
    const [timeoutId, setTimeoutId] = useState(null);

    const [complianceHoursComplete, setComplianceHoursComplete] = useState(false);
    const [complianceEnrollmentsComplete, setComplianceEnrollmentsComplete] = useState(false);
    const [complianceCompareComplete, setComplianceCompareComplete] = useState(false);
    const [isComplianceApproved, setIsComplianceApproved] = useState(false);

    const [retroPeriods, setRetroPeriods] = useState([]);
    const [selectedRetroDate, setSelectedRetroDate] = useState(null);
    const [isLoadingRetroPeriods, setIsLoadingRetroPeriods] = useState(false);
    const [isInitialComplianceComplete, setIsInitialComplianceComplete] = useState(false);
    const [isRunningInitialCompliance, setIsRunningInitialCompliance] = useState(false);
    const noRetroPlaceholder = "No Retro Period";

    const [doShowConfirm, setDoShowConfirm] = useState(false);
    const [confirmCloseAction, setConfirmCloseAction] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [confirmButtonsDisabled, setConfirmButtonsDiabled] = useState(false);

    const steps = [
        {
            label: 'Hours',
            content: (isViewOnly, completedMessage) =>
                <ComplianceHours 
                    isActive={activeStep === 0} 
                    complianceId={compliance?.complianceId} 
                    setComplianceHoursComplete={setComplianceHoursComplete} 
                    isViewOnly={isViewOnly} 
                    completedMessage={completedMessage}
                />,
            isViewable: true,
        },
        {
            label: 'Census',
            content: (isViewOnly, completedMessage) =>
                <ComplianceEnrollments 
                    isActive={activeStep === 1} 
                    complianceId={compliance?.complianceId} 
                    setComplianceEnrollmentsComplete={setComplianceEnrollmentsComplete} 
                    isViewOnly={isViewOnly} 
                    completedMessage={completedMessage}
                />,
            isViewable: true,
        },
        {
            label: 'Initial Compliance',
            content: 
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2}}>
                        {isLoadingRetroPeriods ?
                            <LoaderAnimation />
                        : retroPeriods && retroPeriods.length > 0 ?
                            <FormControl variant="standard" sx={{ m: 1, minWidth: 240 }}>
                                <InputLabel id="retros-label">Earliest Retro Period</InputLabel>
                                <Select
                                    value={selectedRetroDate}
                                    onChange={e => setSelectedRetroDate(e.target.value)}
                                    placeholder="Select Retro Period"
                                    variant="standard"
                                    label="Earliest Retro Period"
                                    labelId="retros-label"
                                    disabled={isLoadingRetroPeriods || isRunningInitialCompliance || isInitialComplianceComplete}
                                >
                                    <MenuItem key={-1} value={noRetroPlaceholder}>{noRetroPlaceholder}</MenuItem>
                                    {retroPeriods.map((item, i) => {
                                        let date = formatDate(item);

                                        return (
                                            <MenuItem key={i} value={date}>
                                                {date}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            : <Box>No Retro Hours Loaded</Box>
                        }
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2}}>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={isRunningInitialCompliance || isInitialComplianceComplete}
                            onClick={() => promptRunInitialCompliance()}
                        >
                            Run Initial Compliance
                        </Button>
                    </Box>
                    {isInitialComplianceComplete ?
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        Click Next to View Initial Compliance Results
                    </Box>
                    : isRunningInitialCompliance ?
                    <LoaderAnimation /> : null}
                </Box>
        },
        {
            label: 'Compare Previous Month',
            content: (isViewOnly, completedMessage) =>
                <ComplianceCompare
                    isActive={activeStep === 3}
                    complianceId={compliance?.complianceId}
                    setComplianceCompareComplete={setComplianceCompareComplete} 
                    isViewOnly={isViewOnly} 
                    completedMessage={completedMessage}
                />
        },
        {
            label: 'Complete',
            content: () =>
                <CompleteCompliance 
                    isActive={activeStep === 4} 
                    complianceId={compliance?.complianceId} 
                    isComplianceApproved={isComplianceApproved}
                    setIsComplianceApproved={setIsComplianceApproved}
                />,
        }];

    useEffect(() => {
        if (isVisible && companyId) {
            setIsLoading(true);
            apiGet(apiUrls.getCompliance(companyId)).then(response => {
                if (response.data) {
                    setCompliance(response.data);
                    setLastCompletedSteps(response.data.completedSteps);
                    setInProgressStep(response.data.inProgressStep);
                }
                else {
                    setCompliance(null);
                    setLastCompletedSteps(null);
                    setInProgressStep(null);
                }
            }).catch(response => {
                console.log(response, "error");
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred loading compliance.");
                error.setDoShowError(true);
            }).finally(() => {
                setIsLoading(false);
            });
        }

        if (timeoutId) {
            setIsPolling(false);
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }
    },[companyId, isVisible])

    useEffect(() => {
        let newActiveStep = 0;
        if (lastCompletedSteps && lastCompletedSteps.length > 0) {
            newActiveStep = Math.max(...lastCompletedSteps.map(s => s.stepNumber)) + 1;
        }
        
        setActiveStep(newActiveStep);
    },[lastCompletedSteps]);

    useEffect(() => {
        if (inProgressStep) {
            switch(activeStep) {
                case 2:
                    if (!inProgressStep.metadataJSON) {
                        setIsPolling(true);
                    }
                break;
            }
        }
    },[activeStep, inProgressStep]);

    const isNextDisabled = useMemo(() => {
        switch (activeStep) {
            case 0:
                return complianceHoursComplete;
            case 1:
                return complianceEnrollmentsComplete;
            case 2:
                return !(isInitialComplianceComplete);
            case 3:
                return complianceCompareComplete;
            case 4:
                return !isComplianceApproved;
            default:
                return false;
        }
    },[activeStep, complianceHoursComplete, complianceEnrollmentsComplete, isInitialComplianceComplete, complianceCompareComplete, isComplianceApproved]);

    useEffect(() => {
        if (activeStep == 2) {
            if (compliance) {
                if (compliance.inProgressStep) {
                    setIsRunningInitialCompliance(true);
                }
                else {
                    getRetroPeriods();
                }
            }
        }
    },[activeStep,compliance])

    const getRetroPeriods = () => {
        if (compliance) {
            setIsLoadingRetroPeriods(true);
            setRetroPeriods([]);

            apiGet(apiUrls.getRetroPeriods(compliance.complianceId)).then(response => {
                if (response.data) {
                    setRetroPeriods(response.data);
                }
            }).catch(response => {
                console.log(response, "error");
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred loading details.");
                error.setDoShowError(true);
            }).finally(() => {
                setIsLoadingRetroPeriods(false);
            });
        }
    }

    useEffect(() => {
        if (retroPeriods && retroPeriods.length > 0) {
            let value = formatDate(retroPeriods[0]);
            setSelectedRetroDate(value);
        }
    },[retroPeriods]);

    const promptStart = () => {
        setConfirmCloseAction(() => (result) => handleStart(result));
        setConfirmTitle('Start Compliance');
        setConfirmText('Are you sure you would like to start compliance for next H&W Benefit Period');
        setDoShowConfirm(true);
    }

    const handleStart = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);
    
            setIsLoading(true);
            saveCompliance({ },
                (response) => {
                    if (response.data) {
                        setCompliance(response.data);
                        setLastCompletedSteps(response.data.steps);
                    }
                },
                () => {
                    setDoShowConfirm(false);
                    setIsLoading(false);
                    setConfirmButtonsDiabled(false);
                });
        }
        else {
            setDoShowConfirm(false);
        }
    }

    const promptNext = () => {
        setConfirmCloseAction(() => (result) => handleNext(result));
        setConfirmTitle('Complete Step');
        setConfirmText('Are you sure you would like to complete this step?');
        setDoShowConfirm(true);
    }

    const handleNext = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);

            saveComplianceStep({ 
                complianceId: compliance.complianceId,
                statusId: 147,
                stepNumber: activeStep,
                metadataJSON: "",
            }, 
            (response) => {
                if (response.data) {
                    setLastCompletedSteps((prevLastCompletedSteps) => {
                        let prev = prevLastCompletedSteps ? [...prevLastCompletedSteps] : [];
                        prev.push(response.data);

                        return prev;
                    });
                }

                setDoShowConfirm(false);
                setConfirmButtonsDiabled(false);
            }, null);
        }
        else {
            setDoShowConfirm(false);
        }
    };
    
    const promptBack = () => {
        setConfirmCloseAction(() => (result) => handleBack(result));
        setConfirmTitle('Undo Step');
        setConfirmText('Are you sure you would like to undo this step?');
        setDoShowConfirm(true);
    }

    const handleBack = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);

            saveComplianceStep({ 
                complianceId: compliance.complianceId,
                statusId: 148,
                stepNumber: activeStep - 1,
                metadataJSON: "",
            }, 
            (response) => {
                setLastCompletedSteps((prevLastCompletedSteps) => {
                    let prev = [...prevLastCompletedSteps];
                    prev.pop();

                    return prev;
                });

                setDoShowConfirm(false);
                setConfirmButtonsDiabled(false);
            }, null);
        }
        else {
            setDoShowConfirm(false);
        }
    };
    
    const handleConfirmClose = (result) => {
        confirmCloseAction(result);
    }

    const promptRestart = () => {
        setConfirmCloseAction(() => (result) => handleRestart(result));
        setConfirmTitle('Restart Compliance');
        setConfirmText('Are you sure you would like to restart compliance for this H&W Benefit Period');
        setDoShowConfirm(true);
    }

    const handleRestart = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);

            saveCompliance({ 
                complianceId: compliance.complianceId,
                statusId: 148
            }, 
            (response) => {
                if (response.data) {
                    resetUI();
                }
            },
            () => {
                setDoShowConfirm(false);
                setConfirmButtonsDiabled(false);
            });
        }
        else {
            setDoShowConfirm(false);
        }
    }

    const promptRunInitialCompliance = () => {
        setConfirmCloseAction(() => (result) => handleRunInitialCompliance(result));
        setConfirmTitle('Run Initial Compliance');
        setConfirmText(`Are you sure you would like to run initial compliance${selectedRetroDate && selectedRetroDate != noRetroPlaceholder ? ` with retro date ${selectedRetroDate}` : ''}?`);
        setDoShowConfirm(true);
    }

    const handleRunInitialCompliance = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);
            setIsRunningInitialCompliance(true);
            setInProgressStep(null);

            let retroDateParam = selectedRetroDate != noRetroPlaceholder ? selectedRetroDate : null;

            apiPost(apiUrls.runInitialCompliance(compliance.complianceId, retroDateParam)).then(response => {
                if (response.data) {
                    setInProgressStep(response.data);
                }
                else {
                    error.setTitle("An Error Occurred");
                    error.setText("Unable to start initial compliance run.");
                    error.setDoShowError(true);    
                    setIsRunningInitialCompliance(false);
                }
            }).catch(response => {
                console.log(response, "error");
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred starting initial compliance run.");
                error.setDoShowError(true);
            }).finally(() => {
                setDoShowConfirm(false);
                setIsLoading(false);
                setConfirmButtonsDiabled(false);
            });
        }
        else {
            setDoShowConfirm(false);
        }
    }

    useEffect(() => {
        if (isPolling && !timeoutId) {
            poll();
        }
    },[isPolling, timeoutId]);

    const poll = () => {
        apiGet(apiUrls.getInitialComplianceStatus(compliance.complianceId)).then(response => {
            if (response.data) {
                setIsRunningInitialCompliance(false);
                setIsInitialComplianceComplete(true);
                setIsPolling(false);
            }
            else {
                let id = setTimeout(poll, 30000);
                setTimeoutId(id);
            }
        });
    }

    const promptComplete = () => {
        setConfirmCloseAction(() => (result) => handleComplete(result));
        setConfirmTitle('Complete Compliance');
        setConfirmText('Are you sure you would like to complete compliance?');
        setDoShowConfirm(true);
    }

    const handleComplete = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);

            apiPost(apiUrls.completeCompliance(compliance.complianceId)).then(response => {
                if (response.data) {
                    resetUI();
                }
                else {
                    error.setTitle("An Error Occurred");
                    error.setText("Unable to complete compliance.");
                    error.setDoShowError(true);
                }
            }).catch(response => {
                console.log(response, "error");
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred completing compliance.");
                error.setDoShowError(true);
            }).finally(() => {
                setDoShowConfirm(false);
                setConfirmButtonsDiabled(false);
            });
        }
        else {
            setDoShowConfirm(false);
        }
    };

    const saveCompliance = (step, thenAction, finallyAction) => {
        save(apiUrls.saveCompliance(companyId), step, thenAction, finallyAction);
    }

    const saveComplianceStep = (step, thenAction, finallyAction) => {
        save(apiUrls.saveComplianceStep(companyId), step, thenAction, finallyAction);
    }

    const save = (url, step, thenAction, finallyAction) => {
        apiPost(url, step).then(response => {
            if(thenAction) {
                thenAction(response);
            }
        }).catch(response => {
            console.log(response, "error");
            error.setTitle("An Error Occurred");
            error.setText("An Error occurred saving compliance.");
            error.setDoShowError(true);
        }).finally(() => {
            if (finallyAction) {
                finallyAction();
            }
        });
    }

    const getTooltipText = (step) => {
        return `Completed by ${step.createUser} on ${formatDate(step.createDate)} at ${moment(step.createDate).format("h:mm:ss a")}`;
    }

    const resetUI = () => {
        // TODO: move reset to children
        setCompliance(null);
        setLastCompletedSteps(null);
        setInProgressStep(null);
        setIsPolling(null);
        setTimeoutId(null);
        
        setRetroPeriods([]);
        setSelectedRetroDate(null);
        setIsLoadingRetroPeriods(false);
        setIsInitialComplianceComplete(false);
        setIsRunningInitialCompliance(false);
    }

    const handleClickStep = (step, index, tooltip) => {
        if (step.isViewable && activeStep > index) {
            setDoShowView(true);

            setViewTitle(step.label);

            var content = step.content instanceof Function ? step.content(true, tooltip) : step.content;
            setViewContent(content);
        }
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 3 }}>
            {isLoading ? <LoaderAnimation /> :
            compliance === null ?
            isReadonly && !isAdmin ?
            <Box>
                Speak with your AXIM representative to complete compliance
            </Box>
            :
            <Box>
                <Button
                    onClick={promptStart}
                    variant="outlined"
                >
                    {"Start Compliance for Next H&W Benefit Date"}
                </Button>
            </Box>
            :
            <Box sx={{ width: '90%'}}>
                <Box>
                    <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                        {`H&W Benefit Date: ${compliance && compliance.hwbenefitDate ? formatDate(compliance.hwbenefitDate) : ''}` }
                    </Typography>
                </Box>
                <Stepper activeStep={activeStep}>
                    {steps.map((step, index) => {
                        let tooltip = "";

                        if (lastCompletedSteps && lastCompletedSteps.length > index) {
                            tooltip = getTooltipText(lastCompletedSteps[index]);
                        }

                        return (
                            <AximTooltip title={tooltip}>
                                <Step key={step.label} onClick={e => handleClickStep(step, index, tooltip)}>
                                    <StepLabel>{step.label}</StepLabel>
                                </Step>
                            </AximTooltip>
                        );
                    })}
                </Stepper>
                {activeStep === steps.length ? (
                    <React.Fragment>
                        <Typography sx={{ mt: 2, mb: 1 }}>
                            {"All steps completed"}
                        </Typography>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <Box sx={{ mt: 2, mb: 1 }}>
                            {steps[activeStep].content instanceof Function ? steps[activeStep].content(false) : steps[activeStep].content}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                            <Button
                                color="secondary"
                                variant="outlined"
                                disabled={activeStep === 0}
                                onClick={promptBack}
                                sx={{ mr: 1 }}
                            >
                                Back
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={promptRestart}
                                sx={{ mr: 1 }}
                            >
                                Restart Compliance
                            </Button>
                            
                            <Box sx={{ flex: '1 1 auto' }} />
                                <Button
                                    onClick={activeStep === steps.length - 1 ? promptComplete : promptNext}
                                    variant="outlined"
                                    disabled={isNextDisabled}
                                >
                                    {activeStep === steps.length - 1 ? "Complete" : "Next"}
                                </Button>
                        </Box>
                    </React.Fragment>
                )}
            </Box>
            }

            <Dialog
                open={doShowView}
                fullWidth={true}
                maxWidth="lg"
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle>{viewTitle}</DialogTitle>
                <DialogContent>
                    {viewContent}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDoShowView(false)} color="primary" variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                doShowConfirm={doShowConfirm}
                handleClose={handleConfirmClose}
                confirmTitle={confirmTitle}
                confirmText={confirmText}
                buttonsDisabled={confirmButtonsDisabled} />
        </Box>
      );
}